<div class="urn__root">
  @if (!minimal()) {
    <!-- ═══ TOP: CONTROLS PANEL ═══ -->
    <div class="urn__controls">
      <!-- Section: Marble counts -->
      <div class="urn__section-label">Marble counts</div>

      @for (bt of ballTypes(); track trackBallType($index, bt)) {
        <div class="urn__slider-row">
          <div class="urn__slider-icon">
            <svg width="18" height="18" viewBox="0 0 18 18">
              @if (bt.pattern === 'hatched') {
                <defs>
                  <pattern
                    [attr.id]="uid + '-icon-' + bt.id"
                    patternUnits="objectBoundingBox"
                    width="0.385"
                    height="0.385"
                    viewBox="0 0 10 10"
                  >
                    <rect width="10" height="10" [attr.fill]="resolvedColorMap()[bt.id]" />
                    <path
                      d="M0,10 L10,0 M-2.5,2.5 L2.5,-2.5 M7.5,12.5 L12.5,7.5"
                      stroke="rgba(255,255,255,0.4)"
                      stroke-width="2.8"
                    />
                  </pattern>
                </defs>
              } @else if (bt.pattern === 'dotted') {
                <defs>
                  <pattern
                    [attr.id]="uid + '-icon-' + bt.id"
                    patternUnits="objectBoundingBox"
                    width="0.269"
                    height="0.269"
                    viewBox="0 0 7 7"
                  >
                    <rect width="7" height="7" [attr.fill]="resolvedColorMap()[bt.id]" />
                    <circle cx="3.5" cy="3.5" r="1.5" fill="rgba(255,255,255,0.55)" />
                  </pattern>
                </defs>
              }
              <circle
                cx="9"
                cy="9"
                r="8"
                [attr.fill]="iconFillMap()[bt.id]"
                [attr.stroke]="iconStrokeMap()[bt.id]"
                stroke-width="1"
              />
            </svg>
          </div>
          <div class="urn__slider-control">
            <dojo-slider
              [param]="bt.id"
              [min]="0"
              [max]="sliderMaxMap()[bt.id]"
              [step]="1"
              [value]="ballCounts()[bt.id]"
              [color]="bt.color"
              [editable]="true"
              [showLabel]="false"
              variant="minimal"
              (valueChange)="onSliderChange(bt.id, $event)"
            />
          </div>
        </div>
      }

      <!-- Section: Draw settings -->
      <div class="urn__section-label">Draw settings</div>

      <div class="urn__slider-row">
        <div class="urn__slider-icon urn__slider-icon--label">Draws</div>
        <div class="urn__slider-control">
          <dojo-slider
            param="draws"
            [min]="1"
            [max]="maxDrawCount"
            [step]="1"
            [value]="drawCount()"
            color="neutral"
            [editable]="true"
            [showLabel]="false"
            variant="minimal"
            (valueChange)="setDrawCount($event)"
          />
        </div>
      </div>

      <div class="urn__slider-row">
        <mat-slide-toggle
          color="primary"
          class="size-m urn__replacement-toggle"
          [checked]="withReplacement()"
          (change)="toggleReplacement($event.checked)"
          labelPosition="after"
        >
          With replacement
        </mat-slide-toggle>
      </div>
    </div>

    <!-- Separator -->
    <div class="urn__separator"></div>
  }

  <!-- ═══ BOTTOM: JAR + TREE SIDE BY SIDE ═══ -->
  <div class="urn__visual">
    <!-- DRAW INDICATORS (grid row 1, col 1) -->
    <div class="urn__draw-shelf">
      @for (col of drawColumns(); track col.position) {
        <div class="urn__draw-chip">
          <span class="urn__draw-chip-label">{{ col.label }}</span>
          @if (col.selectedTypeId; as selId) {
            <svg class="urn__draw-chip-ball" width="22" height="22" viewBox="0 0 22 22">
              <circle
                cx="11"
                cy="11"
                r="10"
                [attr.fill]="resolvedColorMap()[selId]"
                [attr.stroke]="ballStrokeMap()[selId]"
                stroke-width="1"
              />
            </svg>
          } @else {
            <div class="urn__draw-chip-empty"></div>
          }
        </div>
      }
      <div class="urn__draw-actions">
        <dojo-button fill="solid" size="xs" customColor="sky" (click)="chooseBall()" [disabled]="!canDraw()"
          >Draw</dojo-button
        >
        <dojo-icon-button
          class="urn__draw-reset"
          size="xs"
          [icon]="'rotate-right'"
          (click)="resetDraws()"
          [class.urn__draw-reset--visible]="hasDraws()"
          matTooltip="Reset"
        />
      </div>
    </div>

    <!-- JAR SVG (grid row 2, col 1) -->
    <div class="urn__jar-wrapper">
      <svg viewBox="0 0 220 340" preserveAspectRatio="xMidYMid meet" class="urn__svg">
        <defs>
          <!-- Clip path for jar interior -->
          <clipPath [attr.id]="uid + '-jar-clip'">
            <path
              d="
                M 68,30
                L 68,42
                C 68,48 56,54 40,62
                C 26,70 22,78 22,90
                L 22,286
                C 22,306 38,318 58,318
                L 162,318
                C 182,318 198,306 198,286
                L 198,90
                C 198,78 194,70 180,62
                C 164,54 152,48 152,42
                L 152,30
                Z
              "
            />
          </clipPath>

          <!-- Ball patterns per type (objectBoundingBox anchors pattern per ball) -->
          @for (bt of ballTypes(); track trackBallType($index, bt)) {
            <!-- Hatched: diagonal lines drawn directly for clean tiling -->
            @if (bt.pattern === 'hatched') {
              <pattern
                [attr.id]="uid + '-pat-' + bt.id"
                patternUnits="objectBoundingBox"
                width="0.385"
                height="0.385"
                viewBox="0 0 10 10"
              >
                <rect width="10" height="10" [attr.fill]="resolvedColorMap()[bt.id]" />
                <path
                  d="M0,10 L10,0 M-2.5,2.5 L2.5,-2.5 M7.5,12.5 L12.5,7.5"
                  stroke="rgba(255,255,255,0.4)"
                  stroke-width="2.8"
                />
              </pattern>
            }

            <!-- Dotted -->
            @if (bt.pattern === 'dotted') {
              <pattern
                [attr.id]="uid + '-pat-' + bt.id"
                patternUnits="objectBoundingBox"
                width="0.269"
                height="0.269"
                viewBox="0 0 7 7"
              >
                <rect width="7" height="7" [attr.fill]="resolvedColorMap()[bt.id]" />
                <circle cx="3.5" cy="3.5" r="1.5" fill="rgba(255,255,255,0.55)" />
              </pattern>
            }
          }
        </defs>

        <!-- Jar body outline -->
        <path
          class="urn__jar-outline"
          d="
              M 68,30
              L 68,42
              C 68,48 56,54 40,62
              C 26,70 22,78 22,90
              L 22,286
              C 22,306 38,318 58,318
              L 162,318
              C 182,318 198,306 198,286
              L 198,90
              C 198,78 194,70 180,62
              C 164,54 152,48 152,42
              L 152,30
            "
          fill="none"
          stroke="var(--urn-jar-stroke)"
          stroke-width="4"
          stroke-linecap="round"
          stroke-linejoin="round"
        />

        <!-- Lid -->
        <rect
          x="54"
          y="13"
          width="112"
          height="16"
          rx="5"
          fill="none"
          stroke="var(--urn-jar-stroke)"
          stroke-width="4"
        />

        <!-- Balls clipped to jar interior -->
        <g [attr.clip-path]="'url(#' + uid + '-jar-clip)'">
          @for (ball of displayBalls(); track trackBall($index, ball)) {
            <circle
              class="urn__ball"
              [attr.cx]="ball.cx"
              [attr.cy]="ball.cy"
              [attr.r]="ball.r"
              [attr.fill]="ballFillMap()[ball.type.id]"
              [attr.stroke]="ballStrokeMap()[ball.type.id]"
              stroke-width="1.2"
              [attr.opacity]="$index === chosenBallIndex() ? 0 : 1"
            />
          }
        </g>

        <!-- Animated ball (rising/dropping) rendered outside clip -->
        @if (animatedBall(); as anim) {
          <circle
            [attr.cx]="anim.cx"
            [attr.cy]="anim.cy"
            [attr.r]="anim.r"
            [attr.fill]="ballFillMap()[anim.type.id]"
            [attr.stroke]="ballStrokeMap()[anim.type.id]"
            stroke-width="1.2"
          />
        }
      </svg>
    </div>

    <!-- PROBABILITY TREE (grid row 2, col 2) -->
    @if (totalCount() > 0 || hasDraws()) {
      <div class="urn__tree-section">
        <dojo-tree
          [params]="treeParams()"
          [matchingPaths]="selectedTreePath()"
          [matchingPathColors]="selectedTreePathColors()"
          [color]="treeHighlightColor"
        />
      </div>
    }
  </div>
</div>
